/**
 * The pages that hand a video's sound back as a file (SEO_BRIEF_06 section 13):
 * /extract-audio-from-video and /mp4-to-mp3.
 *
 * Each is a tool row like any other, so the hub, the menu, the sitemap and the link graph find it,
 * and each renders `tools/audio_out.html` (server/app/routes_pages.py `AUDIO_OUT_SLUGS`) instead
 * of the drop-options-download shell, because nothing on it is a subtitle file:
 *
 *   extract-audio-from-video   the sound track copied out of the video as it is, no re-encoding
 *                              (src/media/audio-extract.ts). The file type follows the codec, so
 *                              `write_formats` is every type the copy can produce, M4A first.
 *   mp4-to-mp3                 the sound decoded and re-encoded as MP3 at 192 kbit/s by LAME compiled
 *                              to WebAssembly (src/media/mp3-encode.ts). MP3 is its only output. An
 *                              audio file dropped there is converted too.
 *
 * Nothing is uploaded and there is no size cap: the work is web/static/audio, in the visitor's
 * tab. `input: 'video'` says the drop zone asks for a video; it is not a transcribing mode, so the
 * row counts among the tools that run in the browser (`runs_in_browser` in server/app/tools.py).
 *
 * The formats are FILE TYPES, not subtitle formats, which is why this is its own row shape rather
 * than `ToolRow`: `read_formats` is what the drop zone takes, `write_formats` what Save can hand
 * back. `build/tools_export.mjs` carries the rows into `tools.json` like any other channel's.
 */

import type { OptionsSchema } from '../transforms/clean/schema.ts';

/** The videos the drop zone takes, as the other video pages name them. */
export const VIDEO_READS = ['mp4', 'mov', 'mkv', 'webm', 'm4v'] as const;

/** What the copy can save, by codec (audio-extract.ts `planFor`): M4A for AAC, the common case. */
export const COPY_WRITES = ['m4a', 'mp3', 'ogg', 'flac', 'wav', 'mka'] as const;

/** The MP3 page writes MP3 and nothing else; tools/audio_out.html keys its MP3 mode on this. */
export const MP3_WRITES = ['mp3'] as const;

export interface AudioOutRow {
  readonly slug: string;
  readonly label: string;
  readonly op: 'convert';
  readonly ops: readonly string[];
  readonly options_schema: OptionsSchema;
  readonly read_formats: readonly string[];
  readonly write_formats: readonly string[];
  readonly input: 'video';
}

/** No controls: drop, wait, save. */
const NO_OPTIONS: OptionsSchema = { version: 1, groups: [] };
const NO_OPS: readonly string[] = [];

export const AUDIO_OUT_TOOLS: readonly AudioOutRow[] = [
  {
    slug: 'extract-audio-from-video',
    label: 'Extract audio from video',
    op: 'convert',
    ops: NO_OPS,
    options_schema: NO_OPTIONS,
    read_formats: VIDEO_READS,
    write_formats: COPY_WRITES,
    input: 'video',
  },
  {
    slug: 'mp4-to-mp3',
    label: 'MP4 to MP3',
    op: 'convert',
    ops: NO_OPS,
    options_schema: NO_OPTIONS,
    read_formats: VIDEO_READS,
    write_formats: MP3_WRITES,
    input: 'video',
  },
];
